import {
  Button,
  Card,
  DatePicker,
  Descriptions,
  Dropdown,
  Flex,
  Form,
  FormInstance,
  List,
  Modal,
  Popover,
  Select,
  Space,
  Switch,
  Tag,
  Tooltip,
  message,
} from 'antd';
import { useCallback, useContext, useEffect, useMemo, useState, useRef } from 'react';
import APIList from '../../../../http/ApiList';
import { AppContext } from '../../../../contexts/AppContext';
import { GLOBAL_EVENT } from '../../../../consts/common/global-event';
import { COMPARE, EVALUATION_PERIOD, FIELD_CATEGORY, FIELD_IMPORTANT, PAGE_ID } from '../../../../enum/common.enum';
import { groupBy, isEqual, sortBy, sumBy } from 'lodash';
import { getS3Object } from '@meteor/frontend-core';
import { EditOutlined, FilterOutlined, PlusCircleOutlined, SettingOutlined, SwapOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { LabelTag } from '../../../common/label-tag';
import { DraggableTags } from '../../../common/draggable-tags';
import { getDefaultValue } from '../../../../utils/farecast-util';
import { NAME_PATH, useTralitSetting } from '../../../../hooks/useTralitSetting';
import { useLabel } from '../../../../hooks/useLabel';

const CompareHeader: React.FC<any> = (props) => {
  const { property } = props;

  const { event$, getCalenderMap, tralitSettings } = useContext(AppContext);
  const { fontSize } = useTralitSetting({ namePath: NAME_PATH.PREFERENCE });
  const label = useLabel();
  const [form] = Form.useForm();
  const compare = Form.useWatch('compare', form);
  // info展示数据
  const [infoItems, setInfoItems] = useState([]);
  // URL Params
  const searchParams = new URLSearchParams(location.search);
  // 比较Drill Down Field Key
  const compareDrillKey = useMemo(() => {
    if (property.condition && property.condition.compareDrillKey) {
      return property.condition.compareDrillKey;
    }
    return searchParams.get('compareDrillKey');
  }, [searchParams, property.condition]);
  // URL Param对象
  const searchValues: any = useMemo(() => {
    const params = (property.condition ? property.condition : Object.fromEntries(searchParams)) || {};
    if (Object.keys(params).length > 0) {
      return Object.keys(params).reduce((acc, cur) => {
        if (cur === 'drillFields') {
          Object.keys(params[cur]).forEach((key) => {
            acc[key] = params[cur][key].split(',');
          });
        } else {
          acc[cur] = params[cur];
        }
        return acc;
      }, {});
    }
    return {};
  }, [searchParams, property.condition]);
  // 设置页面打开Flag
  const [isSettingOpen, setSettingOpen] = useState(false);
  // Loading
  const [loading, setLoading] = useState(true);
  // Drill Down Fields
  const [drillFields, setDrillFields] = useState([]);
  // 菜单的KPI Fields
  const [kpiFields, setKpiFields] = useState([]);
  // 下拉框数据
  const [options, setOptions] = useState({
    fiscalYear: ['FY23', 'FY24', 'FY25', 'FY26', 'FY27'].map((value) => ({ key: value, label: value, value })),
    evaluationPeriod: ['今月単月', '先週単週', '全期', '上期', '下期', '今月まで', '実績のみ', '見込のみ'].map((value) => ({
      key: value,
      label: value,
      value,
    })),
    compare: [
      {
        key: 'snapshot',
        value: 'snapshot',
        label: '断面',
      },
      {
        key: 'period',
        value: 'period',
        label: '期間',
      },
      {
        key: 'plan',
        value: 'plan',
        label: label.plan,
      },
      {
        key: 'prev_year',
        value: 'prev_year',
        label: '前年',
      },
      {
        key: 'prev_month',
        value: 'prev_month',
        label: '前月',
      },
    ],
    snapshots: [],
    metric: [],
  });

  // 保存的Columns设置
  const [drillSaveFields, setDrillSaveFields] = useState<any[]>([]);

  useEffect(() => {
    form.setFieldValue('drillSaveFields', drillSaveFields);
  }, [drillSaveFields]);

  /**
   * 根据比较对象得到联动表单
   */
  const CompareComponent = useCallback(() => {
    const isSameTheIntervalOfTwoDatePeriods = (firstData: any, secondData: any) => {
      if (secondData.length === 0) {
        return true;
      }

      const firstStartDate = dayjs(firstData[0]);
      const firstEndDate = dayjs(firstData[1]);
      const secondStartDate = dayjs(secondData[0]);
      const secondEndDate = dayjs(secondData[1]);
      const firstDiff = firstEndDate.diff(firstStartDate, 'month');
      const secondDiff = secondEndDate.diff(secondStartDate, 'month');

      return firstDiff === secondDiff;
    };

    const map = {
      snapshot: (
        <>
          <Form.Item name="targetSnapshot" noStyle>
            <Select style={{ width: 120 }} options={options.snapshots} />
          </Form.Item>
        </>
      ),
      period: (
        <>
          <Space split="~">
            <Form.Item name="currentPeriod" label="前期間" rules={[{ required: true }]} noStyle>
              <DatePicker.RangePicker
                picker="month"
                style={{ width: 180 }}
                onChange={() => {
                  const curDate = form.getFieldValue('currentPeriod') || [];
                  const tarDate = form.getFieldValue('targetPeriod') || [];
                  if (!isSameTheIntervalOfTwoDatePeriods(curDate, tarDate)) {
                    message.error('2つの期間の間隔は一致する必要があります', 5);
                    form.setFieldValue('currentPeriod', []);
                  }
                }}
              />
            </Form.Item>
            <Form.Item name="targetPeriod" label="後期間" rules={[{ required: true }]} noStyle>
              <DatePicker.RangePicker
                picker="month"
                style={{ width: 180 }}
                onChange={() => {
                  const curDate = form.getFieldValue('currentPeriod') || [];
                  const tarDate = form.getFieldValue('targetPeriod') || [];
                  if (!isSameTheIntervalOfTwoDatePeriods(curDate, tarDate)) {
                    message.error('2つの期間の間隔は一致する必要があります', 5);
                    form.setFieldValue('targetPeriod', []);
                  }
                }}
              />
            </Form.Item>
          </Space>
        </>
      ),
      plan: <></>,
      prev_year: <></>,
      prev_month: <></>,
    };
    return (
      <>
        <Form.Item label={'比較対象'}>
          <Space>
            <Form.Item name="compare" noStyle>
              <Select style={{ width: 120 }} options={options.compare} />
            </Form.Item>
            {map[compare]}
          </Space>
        </Form.Item>
      </>
    );
  }, [form, options, compare]);

  /**
   * 获取比较Drill Down
   */
  const getCompareField = useCallback((drillFields) => {
    const sorted = sortBy(drillFields, 'level');
    if (drillFields.length > 0) {
      if (compareDrillKey) {
        return sorted[drillFields.findIndex((drillField) => drillField.fieldKey === compareDrillKey) + 1];
      }
      return sorted[sorted.length - 1];
    }
    return {};
  }, []);

  /**
   * 初始化
   */
  useEffect(() => {
    setLoading(true);
    APIList.getHeader()
      .post({
        websocketRoute: 'WebsocketOpenaiEvent',
        client_account: tralitSettings.account,
      })
      .then(async (res) => {
        const response = await getS3Object(process.env.REACT_APP_BUCKET_NAME, res.bucketKey);
        const { snapshot: currentSnapshot, snapshots, fields } = response;
        // 获取目标断面(断面列表已排序，取上一个断面就是目标断面)
        const targetSnapshot = snapshots[snapshots.findIndex((snapshot) => snapshot === currentSnapshot) + 1];

        // Drill Fields
        const drillFields = fields.filter(
          (field) => field.category === FIELD_CATEGORY.DRILL_DOWN && field.classification?.includes('risk')
        );
        setDrillFields(drillFields);
        // KPI Fields
        const kpiFields = fields.filter((field) => field.category === FIELD_CATEGORY.KPI_DATA && field.isMenu);
        setKpiFields(kpiFields);
        // 默认比较KPI Field
        const kpiInfo = kpiFields.find((field) => field.category === FIELD_CATEGORY.KPI_DATA && field.isCompare);
        // 默认比较KPI Field Key
        const metric = kpiInfo?.fieldKey;
        // 设置Options
        setOptions((pref) => ({
          ...pref,
          snapshots: snapshots.map((snapshot) => ({ value: snapshot, label: snapshot })),
          metric: kpiFields.map((kpiField) => ({ value: kpiField.fieldKey, label: kpiField.label })),
        }));
        // 设置保存的Drill Down Fields
        const drillSaveFields = drillFields.filter((drillField) => drillField.important > FIELD_IMPORTANT.LEVEL0 && drillField.isCompare);
        // 计算集計期間
        const aggregationPeriod = getAggregationPeriod(
          currentSnapshot,
          getDefaultValue(tralitSettings.account, 'fiscalYear'),
          EVALUATION_PERIOD.FULL_PERIOD
        );
        const fieldsValues = {
          compare: searchValues.compare || COMPARE.PLANNED_RATIO,
          targetSnapshot: searchValues.targetSnapshot || targetSnapshot,
          currentSnapshot: searchValues.currentSnapshot || currentSnapshot,
          fiscalYear: searchValues.fiscalYear || getDefaultValue(tralitSettings.account, 'fiscalYear'),
          evaluationPeriod: searchValues.evaluationPeriod || EVALUATION_PERIOD.FULL_PERIOD,
          aggregationPeriod,
          drillFields: {
            ...drillFields.reduce((acc, cur) => {
              acc[cur.fieldKey] =
                searchValues && searchValues[cur.fieldKey] ? searchValues[cur.fieldKey] : cur.options.map((option) => option);
              return acc;
            }, {}),
          },
          metric: searchValues.compareKpiKey || metric,
          drillSaveFields:
            (searchValues.compareDrillKeys &&
              searchValues.compareDrillKeys.map((drillSaveFieldKey) => {
                const drillField = drillFields.find((drillField) => drillField.fieldKey === drillSaveFieldKey);
                return drillField;
              })) ||
            drillSaveFields,
        };
        // 设置Form数据
        form.setFieldsValue(fieldsValues);
        setDrillSaveFields(drillSaveFields);
        // 设置Desc数据
        setDesc(fieldsValues, drillFields);
        event$.emit({
          eventType: GLOBAL_EVENT.PAGE_INIT,
          data: fieldsValues,
          pageId: PAGE_ID.RISK,
        });
        setLoading(false);
      });
  }, [tralitSettings.account]);

  /**
   * 设置Desc
   * @param data
   */
  const setDesc = (data, drillFields) => {
    // 根据value获取下拉框的label
    const getOptionLabel = (key, value) => {
      return options[key]?.find((option) => option.value === value)?.label;
    };

    setInfoItems([
      {
        key: 'currentSnapshot',
        label: '断面',
        children: data.currentSnapshot,
      },
      {
        key: 'fiscalYear',
        label: '財務年度',
        children: getOptionLabel('fiscalYear', data.fiscalYear),
      },
      ...drillFields.reduce((acc, drillField) => {
        const findField = drillFields.find((field) => drillField.fieldKey === field.fieldKey);
        if (!isEqual(sortBy(findField.options), sortBy(data.drillFields[drillField.fieldKey]))) {
          acc.push({
            key: drillField.fieldKey,
            label: drillField.label,
            children: data.drillFields ? data.drillFields[drillField.fieldKey]?.join(', ') : '',
          });
        }
        return acc;
      }, []),
    ]);
  };

  /**
   * 通过日历获取集計期間
   */
  const getAggregationPeriod = (snapshot, fiscalYear, evaluationPeriod) => {
    const calenderMap = getCalenderMap(snapshot);
    if (calenderMap && calenderMap[fiscalYear]) {
      return calenderMap[fiscalYear][evaluationPeriod].period;
    }
    return null;
  };

  /**
   * 評価期間修改联动集計期間
   */
  const evaluationPeriodChange = (values) => {
    if (values.evaluationPeriod || values.fiscalYear) {
      const aggregationPeriod = getAggregationPeriod(
        form.getFieldValue('currentSnapshot'),
        form.getFieldValue('fiscalYear'),
        form.getFieldValue('evaluationPeriod')
      );
      form.setFieldValue('aggregationPeriod', aggregationPeriod);
    }
  };

  const DrillFieldsComponent = useCallback(() => {
    return (
      <>
        {drillFields.map(({ fieldKey, label, options }) => (
          <Form.Item key={fieldKey} name={['drillFields', fieldKey]} label={label} labelCol={{ span: 6 }} layout="horizontal">
            <Select
              allowClear
              mode="multiple"
              maxTagCount="responsive"
              style={{ width: 220 }}
              showSearch
              filterOption={(input, option) => ((option?.label as string) ?? '').toLowerCase().includes(input.toLowerCase())}
              options={sortBy(options).map((item) => ({
                key: item,
                label: item,
                value: item,
              }))}
            />
          </Form.Item>
        ))}
      </>
    );
  }, [drillFields]);

  const KpiFieldComponent = useCallback(() => {
    return (
      <Form.Item key={'kpiField'} name={'metric'} label={'KPI'} labelCol={{ span: 6 }}>
        <Select style={{ width: 220 }} options={options.metric} />
      </Form.Item>
    );
  }, [kpiFields]);

  const DrillDownsComponent = useCallback(() => {
    const balanceDrillFields = drillFields.reduce((acc, drillField) => {
      if (drillSaveFields.findIndex((drillSaveField) => drillSaveField.fieldKey === drillField.fieldKey) === -1) {
        acc.push({
          ...drillField,
          key: drillField.fieldKey,
          onClick: ({ key }) => {
            setDrillSaveFields((pref) => [...pref, drillFields.find((drillField) => drillField.fieldKey === key)]);
          },
        });
      }
      return acc;
    }, []);
    return (
      <>
        <Form.Item
          name="drillSaveFields"
          label="ドリルダウン軸"
          rules={[
            {
              required: true,
              message: 'ドリューを選択してください！',
            },
            {
              validator: async (_, drillSaveFields) => {
                const hasErr = drillSaveFields.some((drillSaveField) => {
                  const parIndex = drillSaveFields.findIndex((field) => field.fieldKey === drillSaveField.parentKey);
                  const nurIndex = drillSaveFields.findIndex((field) => field.fieldKey === drillSaveField.fieldKey);
                  if (parIndex > nurIndex) {
                    return true;
                  }
                  return false;
                });
                if (hasErr) {
                  return Promise.reject(new Error('親子関係に基づいて並べ替えてください。'));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <DraggableTags
            items={drillSaveFields.map((drillSaveField) => ({
              id: drillSaveField.fieldKey,
              text: drillSaveField,
            }))}
            labelProp="label"
            closable
            onClose={(items) => {
              setDrillSaveFields(items.map((item) => item.text));
            }}
            onDragEnd={(items) => {
              setDrillSaveFields(items.map((item) => item.text));
            }}
          />
          {balanceDrillFields && balanceDrillFields.length > 0 && (
            <Dropdown menu={{ items: balanceDrillFields }} trigger={['click']}>
              <a
                onClick={(e) => {
                  e.preventDefault();
                }}
              >
                <Space>
                  <PlusCircleOutlined />
                </Space>
              </a>
            </Dropdown>
          )}
        </Form.Item>
      </>
    );
  }, [drillFields, drillSaveFields]);

  const handleOk = () => {
    // validate
    form.validateFields().then(() => {
      const data = form.getFieldsValue();
      const eventInfo = {
        eventType: property.triggerEventType,
        data,
        pageId: PAGE_ID.RISK,
      };
      event$.emit(eventInfo);
      setSettingOpen(false);
      setDesc(data, drillFields);
    });
  };

  return (
    <>
      <Card style={{ height: '100%' }} styles={{ body: { padding: 0, height: '100%' } }}>
        <Flex style={{ height: '100%', alignItems: 'center' }}>
          <Flex style={{ flex: 1, flexDirection: 'row', alignItems: 'center' }}>
            <span style={{ margin: '0px 14px', fontSize: fontSize.title, fontWeight: 'bold' }}>業績リスク分析 </span>
            <Flex gap="4px 0" wrap style={{ flex: 1 }}>
              {infoItems.map((item) => {
                return <LabelTag key={item.key} label={item.label} value={item.children} />;
              })}
            </Flex>
          </Flex>
          <Flex style={{ width: 60, justifyContent: 'center' }}>
            {!loading && (
              <SettingOutlined
                style={{ cursor: 'pointer', fontSize: 18 }}
                onClick={() => {
                  setSettingOpen(true);
                }}
              />
            )}
          </Flex>
        </Flex>
      </Card>
      <Modal
        width={750}
        title="業績リスク分析"
        open={isSettingOpen}
        onOk={handleOk}
        onCancel={() => {
          setSettingOpen(false);
        }}
      >
        <Form
          form={form}
          labelCol={{ span: 6 }}
          onValuesChange={(values) => {
            evaluationPeriodChange(values);
          }}
        >
          <Form.Item name="currentSnapshot" label="Snapshot">
            <Select style={{ width: 120 }} options={options.snapshots} />
          </Form.Item>
          <Form.Item name="fiscalYear" label={'財務年度'}>
            <Select style={{ width: 120 }} options={options.fiscalYear} />
          </Form.Item>
          {compare !== 'period' && (
            <Form.Item label={'評価期間'}>
              <Space>
                <Form.Item name="evaluationPeriod" noStyle>
                  <Select style={{ width: 120 }} options={options.evaluationPeriod} />
                </Form.Item>
                <Form.Item name="aggregationPeriod" noStyle>
                  <DatePicker.RangePicker style={{ width: 240 }} />
                </Form.Item>
              </Space>
            </Form.Item>
          )}
          <CompareComponent />
          <DrillFieldsComponent />
          <KpiFieldComponent />
          <DrillDownsComponent />
        </Form>
      </Modal>
    </>
  );
};

export default CompareHeader;
